<template>
  <el-dialog v-model="visible" title="流程图预览" width="80%" :close-on-click-modal="false" :close-on-press-escape="true"
    class="mermaid-dialog" @close="handleClose">
    <template #header>
      <div class="dialog-header">
        <span class="dialog-title">流程图预览</span>
        <div class="dialog-actions">
          <el-button size="small" @click="retryRender" :loading="loading">
            重新生成
          </el-button>
          <el-button size="small" type="primary" @click="handleSave" :loading="loading || !hasChart || isUploading"
            v-if="hasChart && !error">
            {{ isUploading ? '上传中...' : '保存流程图' }}
          </el-button>
        </div>
      </div>
    </template>
    <div class="mermaid-content">
      <!-- 流程图展示区域 -->
      <div class="chart-section">
        <div v-if="loading" class="loading-container">
          <el-icon class="is-loading">
            <Loading />
          </el-icon>
          <span>正在生成流程图...</span>
        </div>
        <div v-else-if="error" class="error-container">
          <el-icon color="#F56C6C">
            <Warning />
          </el-icon>
          <span>{{ error }}</span>
        </div>
        <div v-show="hasChart" ref="mermaidContainer" class="chart-container"></div>
      </div>
    </div>
  </el-dialog>
</template>
<script setup>
import { ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Loading, Warning } from '@element-plus/icons-vue'
import { fetchUpload, fetchRecordAdd } from '@/api/project'
import html2canvas from 'html2canvas'

// 定义组件的props和emits
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  content: {
    type: String,
    default: ''
  },
  projectId: {
    type: String,
    default: ''
  }
})
const emit = defineEmits(['update:modelValue', 'close', 'save'])

// 响应式数据
const visible = ref(props.modelValue)
const loading = ref(false)
const error = ref('')
const mermaidCode = ref('')
const hasChart = ref(false)
const mermaidContainer = ref(null)
const mermaidInitialized = ref(false)
const isUploading = ref(false)

// 监听visible变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal && props.content) {
    generateMermaid()
  }
})
watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 初始化Mermaid，禁用外部资源
const initMermaid = async () => {
  if (mermaidInitialized.value) return
  try {
    // 动态导入mermaid
    const mermaid = (await import('mermaid')).default
    // 初始化mermaid，使用严格模式避免外部资源
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'strict',  // 严格安全级别
      flowchart: {
        useMaxWidth: true,
        htmlLabels: false,  // 禁用HTML标签
        curve: 'basis'
      },
      fontFamily: 'Arial, sans-serif',
      fetchConfig: {
        mode: 'no-cors'
      }
    })
    mermaidInitialized.value = true
    console.log('Mermaid initialized successfully in strict mode')
  } catch (err) {
    console.error('初始化Mermaid失败:', err)
    error.value = '初始化流程图引擎失败: ' + err.message
  }
}

// 生成Mermaid流程图
const generateMermaid = async () => {
  if (!props.content?.trim()) {
    error.value = '没有内容可以生成流程图'
    return
  }
  loading.value = true
  error.value = ''
  hasChart.value = false
  try {
    await initMermaid()
    const requestBody = {
      description: props.content,
      type: 'flowchart'
    }
    const response = await fetch('/api/mermaid/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
    }
    hasChart.value = true
    const data = await response.json()
    if (data.code === 0 && data.content) {
      mermaidCode.value = data.content
      await renderMermaid(data.content)
    } else {
      hasChart.value = false
      throw new Error(data.message || '生成流程图失败')
    }
  } catch (err) {
    console.error('生成流程图错误:', err)
    hasChart.value = false
    error.value = err.message || '生成流程图失败，请重试'
  } finally {
    loading.value = false
  }
}

// 渲染Mermaid图表
const renderMermaid = async (code) => {
  if (!mermaidContainer.value) {
    console.error('Mermaid容器不存在')
    hasChart.value = false
    error.value = '图表容器未找到'
    return
  }
  try {
    if (!mermaidInitialized.value) {
      await initMermaid()
    }
    const mermaid = (await import('mermaid')).default
    mermaidContainer.value.innerHTML = ''
    const id = `mermaid-${Date.now()}`
    const chartDiv = document.createElement('div')
    chartDiv.id = id
    chartDiv.className = 'mermaid-chart'
    
    try {
      const { svg } = await mermaid.render(id, code)
      chartDiv.innerHTML = svg
      mermaidContainer.value.appendChild(chartDiv)
      ElMessage.success('流程图生成成功')
    } catch (renderError) {
      console.error('Mermaid渲染错误:', renderError)
      hasChart.value = false
      chartDiv.innerHTML = ''
      throw new Error('流程图渲染失败: ' + renderError.message)
    }
  } catch (err) {
    console.error('渲染Mermaid图表错误:', err)
    error.value = '渲染流程图失败: ' + err.message
    hasChart.value = false
  }
}

// 重新生成
const retryRender = () => {
  generateMermaid()
}

// 关闭弹框
const handleClose = () => {
  visible.value = false
  error.value = ''
  mermaidCode.value = ''
  hasChart.value = false
  isUploading.value = false
  if (mermaidContainer.value) {
    mermaidContainer.value.innerHTML = ''
  }
  emit('close')
}

// 终极净化SVG：只保留最基本的图形元素
const sanitizeSvg = (svgElement) => {
  // 创建全新SVG元素
  const cleanSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  
  // 复制基本尺寸和命名空间属性
  const attributesToCopy = ['width', 'height', 'viewBox', 'xmlns', 'xmlns:xlink'];
  attributesToCopy.forEach(attr => {
    const value = svgElement.getAttribute(attr);
    if (value) {
      cleanSvg.setAttribute(attr, value);
    }
  });
  
  // 设置默认尺寸（如果没有指定）
  if (!cleanSvg.getAttribute('width')) {
    cleanSvg.setAttribute('width', '800');
  }
  if (!cleanSvg.getAttribute('height')) {
    cleanSvg.setAttribute('height', '600');
  }
  if (!cleanSvg.getAttribute('viewBox')) {
    const width = cleanSvg.getAttribute('width');
    const height = cleanSvg.getAttribute('height');
    cleanSvg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  }
  
  // 扩展安全元素列表，包含Mermaid图表所需的所有必要元素
  const allowedElements = [
    'svg', 'g', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 
    'text', 'tspan', 'textPath', 'defs', 'clipPath', 'filter', 'linearGradient', 
    'radialGradient', 'stop', 'marker', 'use'
  ];
  
  // 递归复制安全元素
  const copySafeElements = (source, target) => {
    Array.from(source.childNodes).forEach(node => {
      // 跳过文本节点以外的非元素节点
      if (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.TEXT_NODE) {
        return;
      }
      
      // 检查元素是否被允许
      if (node.nodeType === Node.ELEMENT_NODE) {
        const nodeName = node.nodeName.toLowerCase();
        if (!allowedElements.includes(nodeName)) {
          return;
        }
      }
      
      let newNode;
      if (node.nodeType === Node.ELEMENT_NODE) {
        newNode = document.createElementNS(node.namespaceURI, node.nodeName);
        
        // 更精细地过滤属性，保留必要的样式信息
        Array.from(node.attributes).forEach(attr => {
          const attrName = attr.name.toLowerCase();
          const attrValue = attr.value;
          
          // 1. 过滤事件处理器
          if (attrName.startsWith('on')) {
            return;
          }
          
          // 2. 对于href/xlink:href，只过滤危险的外部资源引用
          if ((attrName === 'href' || attrName === 'xlink:href') && 
              attrValue && 
              (attrValue.startsWith('http://') || 
               attrValue.startsWith('https://') || 
               attrValue.startsWith('data:image/'))) {
            return;
          }
          
          // 3. 对于style属性，保留基础样式，移除可能导致跨域问题的url()引用
          if (attrName === 'style') {
            const safeStyle = attrValue.replace(/url\([^)]*\)/g, 'none');
            if (safeStyle.trim()) {
              newNode.setAttribute(attr.name, safeStyle);
            }
          } else if (!attrName.includes('url')) {
            // 复制其他安全属性，包括类名、ID、填充色等样式属性
            newNode.setAttribute(attr.name, attrValue);
          }
        });
        
        // 递归处理子节点
        copySafeElements(node, newNode);
      } else if (node.nodeType === Node.TEXT_NODE) {
        // 直接复制文本内容，确保文字显示
        newNode = document.createTextNode(node.textContent);
      }
      
      if (newNode) {
        target.appendChild(newNode);
      }
    });
  };
  
  copySafeElements(svgElement, cleanSvg);
  return cleanSvg;
};

// 上传图片到服务器
const uploadImageToServer = async (imageData, filename) => {
  try {
    if (!imageData.startsWith('data:image/jpeg;base64')) {
      throw new Error('生成的文件不是有效的JPG格式');
    }
    
    const response = await fetch(imageData);
    const blob = await response.blob();
    const jpgBlob = new Blob([blob], { type: 'image/jpeg' });
    
    const formData = new FormData();
    formData.append('files', jpgBlob, filename);
    formData.append('source', '3');
    formData.append('fileType', '1');
    formData.append('projectId', props.projectId);
    
    const uploadResult = await fetchUpload(formData);
    console.log('上传结果:', uploadResult);
    
    if (uploadResult && uploadResult.id && uploadResult.fileUrl) {
      if (!uploadResult.fileUrl.endsWith('.jpg') && !uploadResult.fileUrl.endsWith('.jpeg')) {
        throw new Error(`服务器返回链接不是JPG格式: ${uploadResult.fileUrl}`);
      }
      return {
        id: uploadResult.id,
        fileUrl: uploadResult.fileUrl || uploadResult.previewUrl
      };
    } else {
      throw new Error('图片上传失败，未返回有效ID和链接');
    }
  } catch (error) {
    console.error('上传图片失败:', error);
    ElMessage.error('上传图片失败: ' + error.message);
    throw error;
  }
};

// 保存流程图（使用 html2canvas 将 SVG 转为 PNG）
const handleSave = async () => {
  if (!mermaidCode.value || !mermaidContainer.value) {
    ElMessage.warning('没有可保存的流程图数据');
    return;
  }
  isUploading.value = true;
  
  try {
    // 1. 获取原始SVG元素
    const svgElement = mermaidContainer.value.querySelector('svg');
    if (!svgElement) {
      throw new Error('未找到流程图SVG元素');
    }
    
    // 2. 确保SVG完全渲染完成后再进行转换
    // 获取SVG的尺寸信息
    const rect = svgElement.getBoundingClientRect();
    const width = rect.width || svgElement.getAttribute('width') || 800;
    const height = rect.height || svgElement.getAttribute('height') || 600;
    
    // 创建临时容器来放置SVG元素
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.width = width + 'px';
    tempContainer.style.height = height + 'px';
    tempContainer.style.backgroundColor = '#ffffff';
    
    // 克隆SVG元素以避免修改原始元素
    const clonedSvg = svgElement.cloneNode(true);
    // 确保克隆的SVG有正确的尺寸
    clonedSvg.setAttribute('width', width);
    clonedSvg.setAttribute('height', height);
    
    tempContainer.appendChild(clonedSvg);
    document.body.appendChild(tempContainer);
    
    // 等待一帧确保元素渲染完成
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    // 使用 html2canvas 转换，添加更多配置确保完整渲染
    const canvas = await html2canvas(tempContainer, {
      backgroundColor: '#ffffff',
      scale: 2, // 提高图像质量
      useCORS: true,
      logging: false,
      width: width,
      height: height,
      scrollX: 0,
      scrollY: 0,
      windowWidth: width,
      windowHeight: height
    });
    
    // 移除临时容器
    document.body.removeChild(tempContainer);
    
    // 3. 将 Canvas 转换为 PNG 数据
    const imageData = canvas.toDataURL('image/png');
    
    // 4. 将 PNG 数据上传到服务器
    const filename = `flowchart_${Date.now()}.png`;
    
    // 创建 Blob 用于上传
    const response = await fetch(imageData);
    const blob = await response.blob();
    const pngBlob = new Blob([blob], { type: 'image/png' });
    
    // 创建 FormData 并上传 PNG
    const formData = new FormData();
    formData.append('files', pngBlob, filename);
    formData.append('source', '3');
    formData.append('fileType', '1');
    formData.append('projectId', props.projectId);
    
    // 上传 PNG 到服务器
    const uploadResult = await fetchUpload(formData);
    console.log('上传结果:', uploadResult);
    
    if (uploadResult && uploadResult.id && uploadResult.fileUrl) {
      // 5. 通知父组件
      emit('save', {
        mermaidCode: mermaidCode.value,
        imageUrl: uploadResult.fileUrl,
        imageId: uploadResult.id,
        description: '流程图：基于技术方案自动生成'
      });
      ElMessage.success('流程图已保存为PNG并上传成功');
      await fetchRecordAdd({
      projectId: props.projectId, 
        opType: "AI_IMAGE_GEN",
        stepName: '要点提纲',
        stepCode: 'step2',
        blockCode: '',
        blockName: '',
        source: "ai",
        description: "AI生成了流程图",
        opDetail: '',
      })
      handleClose();
    } else {
      throw new Error('图片上传失败，未返回有效ID和链接');
    }
  } catch (error) {
    console.error('保存流程图失败:', error);
    
    // 错误处理
    ElMessage.error(`保存失败：${error.message}`);
    isUploading.value = false;
  }
};
</script>
<style scoped>
.mermaid-dialog {
  --el-dialog-margin-top: 5vh;
}
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}
.dialog-actions {
  display: flex;
  gap: 8px;
  margin-right: 40px;
}
.mermaid-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 70vh;
}
.chart-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #fafafa;
  overflow: auto;
  min-height: 400px;
}
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #909399;
}
.error-container {
  color: #F56C6C;
}
.chart-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: auto;
}
/* 确保SVG图表正确显示 */
.chart-container :deep(svg) {
  max-width: 100%;
  max-height: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
}
/* Mermaid图表特定样式 */
.chart-container :deep(.mermaid-chart) {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.chart-container :deep(.mermaid-chart svg) {
  max-width: 100%;
  max-height: 100%;
}
</style>
